import { PocDivider } from 'commons/components/components-wrapper/poc-divider.component';
import { useTranslation } from 'core/i18n/i18n';
import { CustomNotificationFormSubmitButton } from 'modules/settings/components/custom-notifications/custom-notification-form-submit-button';
import { CustomNotificationMultiSelectSection } from 'modules/settings/components/custom-notifications/custom-notification-multi-select-section';
import { CustomNotificationMonitoringPeriodSection } from 'modules/settings/components/custom-notifications/custom-notification-monitoring-period-section';
import { CustomNotificationNameSection } from 'modules/settings/components/custom-notifications/custom-notification-name-section';
import { CustomNotificationThresholdsSection } from 'modules/settings/components/custom-notifications/custom-notification-thresholds-section';
import { CustomNotificationOptionalSettingsSection } from 'modules/settings/components/custom-notifications/custom-notification-optional-settings-section';
import { useCustomAlarmsForm } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/hooks/use-custom-alarms-form/use-custom-alarms-form.hook';
import {
    getCustomAlarmsFormValidationMessages,
    getEventsDropdownValidity,
} from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/settings-devices-custom-alarm-form.validation';
import { CustomNotificationFormLayout } from 'modules/settings/components/custom-notifications/custom-notification-form-layout';
import {
    CUSTOM_ALARM_FORM_HEADER_TEXTS,
    CUSTOM_ALARM_FORM_SUBMIT_TEXTS,
    CUSTOM_ALARM_OPTIONAL_SETTINGS_TEXTS,
} from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-form/settings-devices-custom-alarm-form.constants';

export const CustomAlarmsFormContentContainer: React.FC = () => {
    const { t } = useTranslation();
    const {
        deviceTypeName,
        eventsOptions,
        eventsValid,
        canSubmit,
        form,
        getNameInputError,
        handleClose,
        handleSubmit,
        isLoadingEvents,
        isSubmitting,
        isVisible,
        nameCustomValidationCode,
        setName,
        setResettable,
        submitAttempted,
        windowDaysValidity,
        applyEventSelection,
        setField,
        setNotificationField,
        notificationsTouched,
        mode,
        isFormLoading,
    } = useCustomAlarmsForm();

    const validationMessages = getCustomAlarmsFormValidationMessages(t);

    return (
        <CustomNotificationFormLayout
            visible={isVisible}
            loading={isFormLoading}
            headerTitle={t(CUSTOM_ALARM_FORM_HEADER_TEXTS[mode], { deviceTypeName })}
            onClose={handleClose}
            footer={[
                <CustomNotificationFormSubmitButton
                    key="custom-alarm-submit"
                    labelKey={CUSTOM_ALARM_FORM_SUBMIT_TEXTS[mode]}
                    disabled={!canSubmit}
                    isSubmitting={isSubmitting}
                    onClick={handleSubmit}
                />,
            ]}
        >
            <CustomNotificationNameSection
                name={form.name}
                labelKey="settings.devices.custom-alarms.create.modal.name.label"
                placeholderKey="settings.devices.custom-alarms.create.modal.name.placeholder"
                inputName="customAlarmName"
                autofocus
                validity={getNameInputError(nameCustomValidationCode)}
                validationMessages={validationMessages.name}
                customValidationCode={nameCustomValidationCode}
                onChange={setName}
            />

            <CustomNotificationMultiSelectSection
                name="eventsToTrack"
                titleKey="settings.devices.custom-alarms.create.modal.events.label"
                placeholderKey="settings.devices.custom-alarms.create.modal.events.placeholder"
                options={eventsOptions}
                draftIds={form.eventDraftIds}
                selectedIds={form.selectedEventIds}
                isLoading={isLoadingEvents}
                searchable
                validity={getEventsDropdownValidity(submitAttempted, eventsValid, form.eventDraftIds.length)}
                validationMessages={validationMessages.events}
                onDraftChange={(ids) => setField('eventDraftIds', ids)}
                onApplySelection={applyEventSelection}
                onClear={() => applyEventSelection([])}
            />

            <CustomNotificationMonitoringPeriodSection
                value={form.windowDays}
                labelKey="settings.devices.custom-alarms.create.modal.window.label"
                validity={windowDaysValidity}
                validationMessages={validationMessages.windowDays}
                onChange={(value) => setField('windowDays', value)}
            />

            <PocDivider />

            <CustomNotificationThresholdsSection
                notifications={form.notifications}
                submitAttempted={submitAttempted}
                thresholdsTouched={notificationsTouched}
                onEnabledChange={(notification, enabled) => setNotificationField(notification, 'enabled', enabled)}
                onNumberOfEventsChange={(notification, numberOfEvents) =>
                    setNotificationField(notification, 'numberOfEvents', numberOfEvents)
                }
            />

            <PocDivider />

            <CustomNotificationOptionalSettingsSection
                translationKeys={CUSTOM_ALARM_OPTIONAL_SETTINGS_TEXTS}
                isResettable={form.isResettable}
                requiresCommentOnReset={form.requiresCommentOnReset}
                onResettableChange={setResettable}
                onRequiresCommentOnResetChange={(value) => setField('requiresCommentOnReset', value)}
            />
        </CustomNotificationFormLayout>
    );
};
